import type { DefaultTheme } from 'styled-components';
import { getCulture, uuid } from './helpers';

export interface MadooEditorSavedPayload {
    name: string;
    workflowExternalId: string;
    workflowGuid: string;
    workflowInterfaceId: string;
    requestId?: string;
}

export interface MadooEditorSaveFailedPayload {
    message?: string;
    requestId?: string;
}

export interface MadooRuntimeBridge {
    destroy(): void;
    isReady(): boolean;
    getIframe(): HTMLIFrameElement | null;
}

export interface MadooEditorBridge {
    save(requestId?: string): string;
    run(): void;
    setCustomCss(css: string): void;
    destroy(): void;
    isReady(): boolean;
    getIframe(): HTMLIFrameElement | null;
}

interface MadooEmbedSdk {
    create(options: {
        container: HTMLElement;
        iframeUrl: string;
        token?: string;
        locale?: string;
        theme?: { primaryColor?: string };
        customCss?: string;
        width?: string;
        height?: string;
        allow?: string;
        showName?: boolean;
        showBranding?: boolean;
    }): MadooRuntimeBridge;
    editor: {
        create(options: {
            container: HTMLElement;
            editorUrl: string;
            token: string;
            workflowExternalId?: string;
            name?: string;
            locale?: string;
            theme?: { primaryColor?: string };
            customCss?: string;
            width?: string;
            height?: string;
            allow?: string;
            showName?: boolean;
            showBranding?: boolean;
            onReady?: () => void;
            onSaved?: (payload: unknown) => void;
            onSaveFailed?: (payload: unknown) => void;
        }): MadooEditorBridge;
    };
}

declare global {
    interface Window {
        MadooEmbed?: MadooEmbedSdk;
    }
}

const sdkLoaders = new Map<string, Promise<MadooEmbedSdk>>();

export const getMadooEmbedLocale = () => getCulture() ?? 'en';

export const createMadooEditorSaveRequestId = () => uuid();

export const parseMadooEditorSavedPayload = (data: unknown): MadooEditorSavedPayload | null => {
    if (!data || typeof data !== 'object') return null;
    const payload = data as Record<string, unknown>;
    if (typeof payload.name !== 'string' || !payload.name.trim()) return null;
    if (typeof payload.workflowExternalId !== 'string' || !payload.workflowExternalId.trim()) return null;
    if (typeof payload.workflowGuid !== 'string' || !payload.workflowGuid.trim()) return null;

    return {
        name: payload.name.trim(),
        workflowExternalId: payload.workflowExternalId.trim(),
        workflowGuid: payload.workflowGuid.trim(),
        workflowInterfaceId: typeof payload.workflowInterfaceId === 'string' ? payload.workflowInterfaceId.trim() : '',
        requestId: typeof payload.requestId === 'string' ? payload.requestId : undefined
    };
};

export const parseMadooEditorSaveFailedPayload = (data: unknown): MadooEditorSaveFailedPayload => {
    if (!data || typeof data !== 'object') return {};
    const payload = data as Record<string, unknown>;
    return {
        message: typeof payload.message === 'string' && payload.message.trim() ? payload.message.trim() : undefined,
        requestId: typeof payload.requestId === 'string' ? payload.requestId : undefined
    };
};

export const loadMadooEmbedSdk = (sdkUrl: string): Promise<MadooEmbedSdk> => {
    if (!sdkUrl) return Promise.reject(new Error('Invalid Madoo embed SDK URL.'));
    if (window.MadooEmbed) return Promise.resolve(window.MadooEmbed);
    const existing = sdkLoaders.get(sdkUrl);
    if (existing) return existing;

    const loader = new Promise<MadooEmbedSdk>((resolve, reject) => {
        const script = document.createElement('script');
        script.src = sdkUrl;
        script.async = true;
        script.onload = () => (window.MadooEmbed ? resolve(window.MadooEmbed) : reject(new Error('Madoo embed SDK did not initialize.')));
        script.onerror = () => reject(new Error('Unable to load Madoo embed SDK.'));
        document.head.appendChild(script);
    });
    sdkLoaders.set(sdkUrl, loader);
    return loader;
};

export const buildMadooBackofficeCss = (theme: DefaultTheme) => `
:root {
	--font-sans: ${theme.app.font};
	--background: ${theme.app.backgroundColorBase};
	--color-background: ${theme.app.backgroundColorBase};
	--foreground: ${theme.app.textColor};
	--color-foreground: ${theme.app.textColor};
	--card: ${theme.app.backgroundColor};
	--color-card: ${theme.app.backgroundColor};
	--card-foreground: ${theme.app.textColor};
	--color-card-foreground: ${theme.app.textColor};
	--primary: ${theme.app.secondary};
	--color-primary: ${theme.app.secondary};
	--primary-foreground: #ffffff;
	--color-primary-foreground: #ffffff;
	--secondary: ${theme.app.backgroundToolbar};
	--color-secondary: ${theme.app.backgroundToolbar};
	--secondary-foreground: ${theme.app.textColor};
	--color-secondary-foreground: ${theme.app.textColor};
	--muted: ${theme.app.pageBackgroundColor};
	--color-muted: ${theme.app.pageBackgroundColor};
	--muted-foreground: ${theme.app.primary};
	--color-muted-foreground: ${theme.app.primary};
	--accent: #fef2ed;
	--color-accent: #fef2ed;
	--accent-foreground: ${theme.app.secondary};
	--color-accent-foreground: ${theme.app.secondary};
	--border: ${theme.app.borderColor};
	--color-border: ${theme.app.borderColor};
	--input: ${theme.app.borderColor};
	--color-input: ${theme.app.borderColor};
	--ring: ${theme.app.secondary};
	--color-ring: ${theme.app.secondary};
	--radius: 8px;
}

body,
.madoo-embed,
.madoo-editor-embed {
	background: ${theme.app.backgroundColorBase};
	font-family: ${theme.app.font};
}
`;
